import React, { createContext, useContext, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { ToastContainer } from "@/appComponents/Blocks/Cards/ToastContainer/ToastContainer";
import { Globals } from "@coreapp/CoreApp/Globals";
import { enumUIModes } from "@coreapp/constants";
import { useTopic } from "@/hooks/useTopic";

const TOAST_DELAY = 300; // 300ms minimum delay between toasts
let lastToastTime = 0;

const ToastContext = createContext(null);
const FOOTER_UI_MODES = [enumUIModes.settings];

// these mirror the alert component severities
export const TOAST_STATUS = {
  SUCCESS: "success",
  ERROR: "error",
  INFO: "info",
  WARNING: "warning"
};

export const TOAST_POSITIONS = {
  TOP_LEFT: "top-left",
  TOP_RIGHT: "top-right",
  BOTTOM_LEFT: "bottom-left",
  BOTTOM_RIGHT: "bottom-right",
  BOTTOM_LEFT_FOOTER: "bottom-left-footer"
};

export function isToastGloballyDisabled() {
  return "OC_TOAST_DISABLED" in globalThis && globalThis.OC_TOAST_DISABLED === true;
}

export const ToastProvider = ({ children, position = TOAST_POSITIONS.BOTTOM_LEFT }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback(
    ({ message, content, status = TOAST_STATUS.INFO, alertProps = {}, timeoutMS }) => {
      if (isToastGloballyDisabled()) {
        console.log("Toast disabled globally.");
        return;
      }

      const now = Date.now();
      const timeSinceLastToast = now - lastToastTime;

      // If trying to add toast too quickly, delay it
      if (timeSinceLastToast < TOAST_DELAY) {
        setTimeout(() => {
          addToast({ message, content, status, alertProps, timeoutMS });
        }, TOAST_DELAY - timeSinceLastToast);
        return;
      }

      const id = `toast-${now}`;
      lastToastTime = now;

      setToasts(prev => [...prev, { id, message, content, status, alertProps, timeoutMS }]);
      return id;
    },
    []
  );

  const removeToast = useCallback(id => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const getCorrectPosition = useCallback(position => {
    if (position === TOAST_POSITIONS.BOTTOM_LEFT) {
      const modeHasFooter = FOOTER_UI_MODES.includes(Globals._uiConfig.GetUIMode());
      if (modeHasFooter) {
        return TOAST_POSITIONS.BOTTOM_LEFT_FOOTER;
      }
    }
    return position;
  }, []);

  useTopic("add-toast", payload => {
    addToast(payload);
  });

  const value = { addToast, removeToast };
  const portalContainer = document.getElementById("notification-root");

  return (
    <ToastContext.Provider value={value}>
      {children}
      {portalContainer &&
        createPortal(
          <ToastContainer
            position={getCorrectPosition(position)}
            toasts={toasts}
            onRemove={removeToast}
          />,
          portalContainer
        )}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  return useContext(ToastContext);
};
